/**
 * Hazardous area classification drawings as SVG (plan §5.7, clause 10.2).
 *
 * Exact for every zone shape Extent draws: seen from above, a revolved zone is a circle at its widest
 * radius; seen from the side, it is its profile mirrored about the axis. Whole-room zones are the
 * room's rectangle. Zones use the preferred symbols of Fig. A.1 (Zone 0 small circles, Zone 1 cross
 * hatching, Zone 2 single diagonal hatching) and every sheet carries a symbol key.
 *
 * Pure: no DOM. Paper units are millimetres on an A3 landscape sheet.
 */
import type { ZoneInstance } from '../geometry';
import type { Box, ProjectDoc, ZoneNumber } from '../model/types';

export type DrawingView = 'plan' | 'elevation';

export interface DrawingInput {
  doc: ProjectDoc;
  zones: ZoneInstance[];
  /** Equipment that fails its check, drawn with a heavier outline. */
  failedEquipment: Set<string>;
  view: DrawingView;
  revision: string;
  date: string;
  drawingNumber: string;
  /** Restrict the sheet to one area: its sources, zones, equipment and the plant inside its footprint. */
  areaId?: string;
}

const SHEET = { w: 420, h: 297 };
const MARGIN = 10;
const TITLE = { w: 180, h: 46 };
const KEY_W = 62;
const SCALES = [10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000];
const INK = '#1b2428';
const FAINT = '#7b878c';
const ZONE_STROKE: Record<ZoneNumber, string> = { 0: '#8e1b3a', 1: '#c45a0c', 2: '#b98a10' };

type Rect = { minU: number; maxU: number; minV: number; maxV: number };

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const f = (n: number) => (Math.round(n * 100) / 100).toString();

/** World point → drawing (u, v) in metres for the view: plan = (x, y), elevation from the south = (x, z). */
export const project = (view: DrawingView, p: [number, number, number]): [number, number] => (view === 'plan' ? [p[0], p[1]] : [p[0], p[2]]);

function boxCorners(box: Box): Array<[number, number, number]> {
  const [cx, cy, cz] = box.center;
  const [sx, sy, sz] = box.size;
  const c = Math.cos(box.yawRad), s = Math.sin(box.yawRad);
  const out: Array<[number, number, number]> = [];
  for (const dx of [-0.5, 0.5]) for (const dy of [-0.5, 0.5]) for (const dz of [-0.5, 0.5]) {
    const lx = dx * sx, ly = dy * sy;
    out.push([cx + c * lx - s * ly, cy + s * lx + c * ly, cz + dz * sz]);
  }
  return out;
}

/** Outline of a box in the view, as a polygon (plan: rotated rectangle; elevation: its extent). */
export function boxOutline(view: DrawingView, box: Box): Array<[number, number]> {
  if (view === 'plan') {
    const [cx, cy] = box.center;
    const [sx, sy] = box.size;
    const c = Math.cos(box.yawRad), s = Math.sin(box.yawRad);
    return [[-0.5, -0.5], [0.5, -0.5], [0.5, 0.5], [-0.5, 0.5]].map(([dx, dy]) => [cx + c * dx! * sx - s * dy! * sy, cy + s * dx! * sx + c * dy! * sy]);
  }
  const pts = boxCorners(box).map((p) => project(view, p));
  const us = pts.map((p) => p[0]), vs = pts.map((p) => p[1]);
  const [u0, u1, v0, v1] = [Math.min(...us), Math.max(...us), Math.min(...vs), Math.max(...vs)];
  return [[u0, v0], [u1, v0], [u1, v1], [u0, v1]];
}

/** Zone outline in the view: a circle (plan of a revolved zone) or a polygon. */
export type Outline = { kind: 'circle'; cu: number; cv: number; r: number } | { kind: 'poly'; pts: Array<[number, number]> };

export function zoneOutline(view: DrawingView, z: ZoneInstance): Outline {
  if (z.shape.kind === 'box') return { kind: 'poly', pts: boxOutline(view, z.shape.box) };
  const [ax, ay] = z.shape.axis;
  const profile = z.shape.profile;
  if (view === 'plan') return { kind: 'circle', cu: ax, cv: ay, r: Math.max(...profile.map((p) => p[0])) };
  // elevation from the south: profile on the right of the axis, mirrored on the left
  const right = profile.map(([r, h]): [number, number] => [ax + r, h]);
  const left = profile.slice().reverse().map(([r, h]): [number, number] => [ax - r, h]);
  return { kind: 'poly', pts: [...right, ...left] };
}

function extentOf(view: DrawingView, input: DrawingInput, outlines: Array<{ o: Outline }>): Rect {
  const r: Rect = { minU: Infinity, maxU: -Infinity, minV: Infinity, maxV: -Infinity };
  const add = (u: number, v: number) => { r.minU = Math.min(r.minU, u); r.maxU = Math.max(r.maxU, u); r.minV = Math.min(r.minV, v); r.maxV = Math.max(r.maxV, v); };
  for (const a of input.doc.areas) if (a.box) boxOutline(view, a.box).forEach(([u, v]) => add(u, v));
  for (const o of input.doc.objects) {
    if (o.kind === 'box') boxOutline(view, o.box).forEach(([u, v]) => add(u, v));
    else if (view === 'plan') { add(o.base[0] - o.radiusM, o.base[1] - o.radiusM); add(o.base[0] + o.radiusM, o.base[1] + o.radiusM); }
    else { add(o.base[0] - o.radiusM, o.base[2]); add(o.base[0] + o.radiusM, o.base[2] + o.heightM); }
  }
  for (const s of input.doc.sources) add(...project(view, s.position));
  for (const { o } of outlines) {
    if (o.kind === 'circle') { add(o.cu - o.r, o.cv - o.r); add(o.cu + o.r, o.cv + o.r); }
    else o.pts.forEach(([u, v]) => add(u, v));
  }
  if (view === 'elevation') add(r.minU, 0);
  if (!Number.isFinite(r.minU)) return { minU: -10, maxU: 10, minV: -10, maxV: 10 };
  return r;
}

/** Smallest standard scale (1:n) at which the model fits the drawing frame. */
export function chooseScale(widthM: number, heightM: number, frameWmm: number, frameHmm: number): number {
  for (const n of SCALES) if ((widthM * 1000) / n <= frameWmm && (heightM * 1000) / n <= frameHmm) return n;
  return SCALES[SCALES.length - 1]!;
}

function patterns(): string {
  return `<defs>
  <pattern id="z2" width="2.2" height="2.2" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="2.2" stroke="${ZONE_STROKE[2]}" stroke-width="0.25"/></pattern>
  <pattern id="z1" width="1.8" height="1.8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="1.8" stroke="${ZONE_STROKE[1]}" stroke-width="0.22"/><line x1="0" y1="0" x2="1.8" y2="0" stroke="${ZONE_STROKE[1]}" stroke-width="0.22"/></pattern>
  <pattern id="z0" width="2" height="2" patternUnits="userSpaceOnUse"><circle cx="1" cy="1" r="0.5" fill="none" stroke="${ZONE_STROKE[0]}" stroke-width="0.2"/></pattern>
  <clipPath id="frame"><rect x="${MARGIN}" y="${MARGIN}" width="${SHEET.w - 2 * MARGIN - KEY_W}" height="${SHEET.h - 2 * MARGIN}"/></clipPath>
</defs>`;
}

/** The document and zones restricted to one area, for per-area sheets. */
export function forArea(input: DrawingInput): DrawingInput {
  const area = input.doc.areas.find((a) => a.id === input.areaId);
  if (!area?.box) return input;
  const [cx, cy] = area.box.center;
  const c = Math.cos(-area.box.yawRad), s = Math.sin(-area.box.yawRad);
  const inside = (x: number, y: number) => {
    const lx = c * (x - cx) - s * (y - cy), ly = s * (x - cx) + c * (y - cy);
    return Math.abs(lx) <= area.box!.size[0] / 2 + 0.5 && Math.abs(ly) <= area.box!.size[1] / 2 + 0.5;
  };
  const doc = {
    ...input.doc,
    areas: [area],
    sources: input.doc.sources.filter((x) => x.areaId === area.id),
    equipment: input.doc.equipment.filter((e) => e.areaId === area.id || inside(e.box.center[0], e.box.center[1])),
    objects: input.doc.objects.filter((o) => (o.kind === 'box' ? inside(o.box.center[0], o.box.center[1]) : inside(o.base[0], o.base[1]))),
  };
  return { ...input, doc, zones: input.zones.filter((z) => z.areaId === area.id) };
}

export function drawingSvg(raw: DrawingInput): string {
  const input = raw.areaId ? forArea(raw) : raw;
  const { doc, view } = input;
  const areaName = raw.areaId ? input.doc.areas[0]?.name : undefined;
  const outlines = input.zones.map((z) => ({ z, o: zoneOutline(view, z) }));
  const ext = extentOf(view, input, outlines);
  const frame = { x: MARGIN, y: MARGIN, w: SHEET.w - 2 * MARGIN - KEY_W, h: SHEET.h - 2 * MARGIN };
  const pad = 4; // metres of air around the model
  const wM = ext.maxU - ext.minU + 2 * pad;
  const hM = ext.maxV - ext.minV + 2 * pad;
  const scale = chooseScale(wM, hM, frame.w - 8, frame.h - TITLE.h - 12);
  const k = 1000 / scale; // mm on paper per metre
  const cu = (ext.minU + ext.maxU) / 2, cv = (ext.minV + ext.maxV) / 2;
  const ox = frame.x + frame.w / 2, oy = frame.y + (frame.h - TITLE.h - 6) / 2;
  const X = (u: number) => ox + (u - cu) * k;
  const Y = (v: number) => oy - (v - cv) * k;
  const pts = (p: Array<[number, number]>) => p.map(([u, v]) => `${f(X(u))},${f(Y(v))}`).join(' ');
  const out: string[] = [];

  out.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${SHEET.w} ${SHEET.h}" width="${SHEET.w}mm" height="${SHEET.h}mm" font-family="IBM Plex Sans, Arial, sans-serif">`);
  out.push(patterns());
  out.push(`<rect x="0" y="0" width="${SHEET.w}" height="${SHEET.h}" fill="#fff"/>`);
  out.push(`<rect x="${frame.x}" y="${frame.y}" width="${SHEET.w - 2 * MARGIN}" height="${frame.h}" fill="none" stroke="${INK}" stroke-width="0.5"/>`);
  out.push(`<g clip-path="url(#frame)">`);

  if (view === 'elevation') out.push(`<line x1="${frame.x}" y1="${f(Y(0))}" x2="${frame.x + frame.w}" y2="${f(Y(0))}" stroke="${INK}" stroke-width="0.35"/>`);

  // zones: Zone 2 first, then 1, then 0. Higher zones are masked white so they read as one region.
  for (const zone of [2, 1, 0] as const) {
    const these = outlines.filter((x) => x.z.zone === zone);
    for (const fill of zone === 2 ? [`url(#z2)`] : ['#fff', `url(#z${zone})`]) {
      for (const { o } of these) {
        if (o.kind === 'circle') out.push(`<circle cx="${f(X(o.cu))}" cy="${f(Y(o.cv))}" r="${f(o.r * k)}" fill="${fill}" stroke="none"/>`);
        else out.push(`<polygon points="${pts(o.pts)}" fill="${fill}" stroke="none"/>`);
      }
    }
    for (const { o } of these) {
      const stroke = `stroke="${ZONE_STROKE[zone]}" stroke-width="0.35" fill="none"`;
      if (o.kind === 'circle') out.push(`<circle cx="${f(X(o.cu))}" cy="${f(Y(o.cv))}" r="${f(o.r * k)}" ${stroke}/>`);
      else out.push(`<polygon points="${pts(o.pts)}" ${stroke}/>`);
    }
  }

  // geometry
  for (const a of doc.areas) {
    if (!a.box) continue;
    const dash = a.kind === 'outdoor' ? ' stroke-dasharray="3 1.5"' : '';
    out.push(`<polygon points="${pts(boxOutline(view, a.box))}" fill="none" stroke="${INK}" stroke-width="${a.kind === 'indoor' ? 0.5 : 0.25}"${dash}/>`);
    const [u0, v1] = [Math.min(...boxOutline(view, a.box).map((p) => p[0])), Math.max(...boxOutline(view, a.box).map((p) => p[1]))];
    out.push(`<text x="${f(X(u0) + 1)}" y="${f(Y(v1) + 3.2)}" font-size="2.4" fill="${INK}">${esc(a.name)}</text>`);
  }
  for (const o of doc.objects) {
    if (o.kind === 'box') out.push(`<polygon points="${pts(boxOutline(view, o.box))}" fill="#f2f4f4" stroke="${FAINT}" stroke-width="0.25"/>`);
    else if (view === 'plan') out.push(`<circle cx="${f(X(o.base[0]))}" cy="${f(Y(o.base[1]))}" r="${f(o.radiusM * k)}" fill="#f2f4f4" stroke="${FAINT}" stroke-width="0.25"/>`);
    else out.push(`<rect x="${f(X(o.base[0] - o.radiusM))}" y="${f(Y(o.base[2] + o.heightM))}" width="${f(2 * o.radiusM * k)}" height="${f(o.heightM * k)}" fill="#f2f4f4" stroke="${FAINT}" stroke-width="0.25"/>`);
  }
  for (const e of doc.equipment) {
    const failed = input.failedEquipment.has(e.id);
    out.push(`<polygon points="${pts(boxOutline(view, e.box))}" fill="#fff" stroke="${failed ? '#b42318' : INK}" stroke-width="${failed ? 0.5 : 0.25}"/>`);
    const [u, v] = project(view, e.box.center);
    out.push(`<text x="${f(X(u) + 1.2)}" y="${f(Y(v) - 1.2)}" font-size="1.9" font-family="IBM Plex Mono, monospace" fill="${failed ? '#b42318' : INK}">${esc(e.tag)}</text>`);
  }
  // sources and extents
  for (const s of doc.sources) {
    const [u, v] = project(view, s.position);
    const x = X(u), y = Y(v);
    out.push(`<g stroke="${INK}" stroke-width="0.3"><circle cx="${f(x)}" cy="${f(y)}" r="0.9" fill="#fff"/><line x1="${f(x - 1.4)}" y1="${f(y)}" x2="${f(x + 1.4)}" y2="${f(y)}"/><line x1="${f(x)}" y1="${f(y - 1.4)}" x2="${f(x)}" y2="${f(y + 1.4)}"/></g>`);
    out.push(`<text x="${f(x + 1.6)}" y="${f(y + 3.2)}" font-size="1.9" font-family="IBM Plex Mono, monospace" fill="${INK}">${esc(s.tag)}</text>`);
    const local = outlines.filter((o) => o.z.sourceId === s.id && o.z.shape.kind === 'lathe');
    const widest = local.sort((a, b) => b.z.zone - a.z.zone)[0];
    if (widest && widest.z.shape.kind === 'lathe') {
      const r = Math.max(...widest.z.shape.profile.map((p) => p[0]));
      const ang = view === 'plan' ? -Math.PI / 6 : 0;
      const ex = X(u + r * Math.cos(ang)), ey = view === 'plan' ? Y(v + r * Math.sin(ang)) : y;
      out.push(`<line x1="${f(x)}" y1="${f(y)}" x2="${f(ex)}" y2="${f(ey)}" stroke="${INK}" stroke-width="0.18" marker-end="url(#arrow)"/>`);
      out.push(`<text x="${f((x + ex) / 2)}" y="${f((y + ey) / 2 - 0.8)}" font-size="1.8" text-anchor="middle" fill="${INK}">r ${r.toFixed(1)} m</text>`);
    }
  }
  out.push(`</g>`);
  out.push(`<defs><marker id="arrow" viewBox="0 0 6 6" refX="5.5" refY="3" markerWidth="3" markerHeight="3" orient="auto"><path d="M0,0 L6,3 L0,6 z" fill="${INK}"/></marker></defs>`);

  // symbol key (10.2: a key on every drawing)
  const kx = SHEET.w - MARGIN - KEY_W + 4;
  let ky = MARGIN + 8;
  out.push(`<line x1="${SHEET.w - MARGIN - KEY_W}" y1="${MARGIN}" x2="${SHEET.w - MARGIN - KEY_W}" y2="${SHEET.h - MARGIN}" stroke="${INK}" stroke-width="0.35"/>`);
  out.push(`<text x="${kx}" y="${ky}" font-size="3" font-weight="600" fill="${INK}">KEY</text>`);
  ky += 6;
  const groups = new Map<string, true>();
  for (const z of input.zones) groups.set(`${z.zone}|${z.gasGroup ?? '—'}|${z.aitC !== undefined ? tClassLabel(z.aitC) : '—'}`, true);
  const rows = [...groups.keys()].sort();
  for (const zone of [0, 1, 2] as const) {
    out.push(`<rect x="${kx}" y="${ky - 3.2}" width="9" height="5" fill="url(#z${zone})" stroke="${ZONE_STROKE[zone]}" stroke-width="0.3"/>`);
    const tags = rows.filter((r) => r.startsWith(`${zone}|`)).map((r) => { const [, g, t] = r.split('|'); return `${g} ${t}`; });
    out.push(`<text x="${kx + 11}" y="${ky}" font-size="2.4" fill="${INK}">Zone ${zone}</text>`);
    if (tags.length) out.push(`<text x="${kx + 11}" y="${ky + 2.8}" font-size="1.9" fill="${FAINT}">${esc(tags.join(', '))}</text>`);
    ky += 9;
  }
  out.push(`<g stroke="${INK}" stroke-width="0.3"><circle cx="${kx + 4.5}" cy="${ky - 1}" r="0.9" fill="#fff"/><line x1="${kx + 3.1}" y1="${ky - 1}" x2="${kx + 5.9}" y2="${ky - 1}"/><line x1="${kx + 4.5}" y1="${ky - 2.4}" x2="${kx + 4.5}" y2="${ky + 0.4}"/></g>`);
  out.push(`<text x="${kx + 11}" y="${ky}" font-size="2.4" fill="${INK}">Source of release</text>`);
  ky += 7;
  out.push(`<rect x="${kx + 2.5}" y="${ky - 3}" width="4" height="3" fill="#fff" stroke="${INK}" stroke-width="0.25"/><text x="${kx + 11}" y="${ky}" font-size="2.4" fill="${INK}">Equipment</text>`);
  ky += 6;
  out.push(`<rect x="${kx + 2.5}" y="${ky - 3}" width="4" height="3" fill="#fff" stroke="#b42318" stroke-width="0.5"/><text x="${kx + 11}" y="${ky}" font-size="2.4" fill="${INK}">Equipment not suitable</text>`);
  ky += 6;
  out.push(`<line x1="${kx}" y1="${ky - 1}" x2="${kx + 9}" y2="${ky - 1}" stroke="${INK}" stroke-width="0.25" stroke-dasharray="3 1.5"/><text x="${kx + 11}" y="${ky}" font-size="2.4" fill="${INK}">Outdoor area</text>`);
  ky += 10;
  const notes = [
    'Zones to IEC 60079-10-1:2020.',
    'Extents from the calculation sheets;',
    'shapes after Annex A.2.',
    view === 'plan' ? 'Zones shown at their widest.' : 'Elevation looking north.',
    'Walls do not cut zones (MD-008).',
  ];
  notes.forEach((n, i) => out.push(`<text x="${kx}" y="${ky + i * 3.4}" font-size="2" fill="${FAINT}">${esc(n)}</text>`));

  // north arrow (plan) and scale bar
  if (view === 'plan') {
    const nx = frame.x + 12, ny = frame.y + 16;
    out.push(`<g fill="${INK}" stroke="${INK}" stroke-width="0.3"><path d="M${nx},${ny - 7} L${nx + 2.6},${ny + 3} L${nx},${ny + 1.4} L${nx - 2.6},${ny + 3} z"/></g><text x="${nx}" y="${ny + 7.5}" font-size="3" text-anchor="middle" fill="${INK}">N</text>`);
  }
  const barM = niceBar(30 / k);
  const bx = frame.x + 8, by = SHEET.h - MARGIN - 8;
  out.push(`<g stroke="${INK}" stroke-width="0.3"><line x1="${bx}" y1="${by}" x2="${f(bx + barM * k)}" y2="${by}"/>${[0, 0.5, 1].map((t) => `<line x1="${f(bx + t * barM * k)}" y1="${by - 1.2}" x2="${f(bx + t * barM * k)}" y2="${by + 1.2}"/>`).join('')}</g>`);
  out.push(`<text x="${bx}" y="${by - 2.2}" font-size="2" fill="${INK}">0</text><text x="${f(bx + barM * k)}" y="${by - 2.2}" font-size="2" text-anchor="middle" fill="${INK}">${barM} m</text>`);

  // title block
  const tx = SHEET.w - MARGIN - KEY_W - TITLE.w, ty = SHEET.h - MARGIN - TITLE.h;
  const meta = doc.meta;
  const cell = (x: number, y: number, w: number, h: number, label: string, value: string, size = 2.8) =>
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="#fff" stroke="${INK}" stroke-width="0.3"/><text x="${x + 1.5}" y="${y + 3}" font-size="1.7" fill="${FAINT}">${esc(label)}</text><text x="${x + 1.5}" y="${y + h - 2}" font-size="${size}" fill="${INK}">${esc(value)}</text>`;
  out.push(cell(tx, ty, TITLE.w, 14, 'PROJECT', `${meta.name}${meta.site ? ` · ${meta.site}` : ''}`, 3.4));
  out.push(cell(tx, ty + 14, TITLE.w, 12, 'TITLE', `Hazardous area classification — ${view === 'plan' ? 'plan' : 'elevation'}${areaName ? `, ${areaName}` : ''}`, 3));
  out.push(cell(tx, ty + 26, 60, 10, 'CLIENT', meta.client ?? '—'));
  out.push(cell(tx + 60, ty + 26, 50, 10, 'DRAWING NO.', input.drawingNumber));
  out.push(cell(tx + 110, ty + 26, 25, 10, 'REV', input.revision));
  out.push(cell(tx + 135, ty + 26, 45, 10, 'SCALE (A3)', `1:${scale}`));
  out.push(cell(tx, ty + 36, 60, 10, 'PREPARED', ''));
  out.push(cell(tx + 60, ty + 36, 50, 10, 'CHECKED', ''));
  out.push(cell(tx + 110, ty + 36, 70, 10, 'DATE', input.date));
  out.push(`</svg>`);
  return out.join('\n');
}

function niceBar(maxM: number): number {
  for (const n of [1, 2, 5, 10, 20, 25, 50, 100, 200, 500]) if (n >= maxM * 0.5 && n <= maxM * 1.5) return n;
  return Math.max(1, Math.round(maxM));
}

function tClassLabel(aitC: number): string {
  return aitC >= 450 ? 'T1' : aitC >= 300 ? 'T2' : aitC >= 200 ? 'T3' : aitC >= 135 ? 'T4' : aitC >= 100 ? 'T5' : aitC >= 85 ? 'T6' : '—';
}
